import React, { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";
import { LIST_MY_BOOKINGS, GET_BOOKING_POLICY } from "apollo/queries/booking";
import { LIST_EVENTS } from "apollo/queries/events";
import { CANCEL_BOOKING } from "apollo/mutations/booking";
import Loader from "components/Spinners/Loader";
import RescheduleModal from "./RescheduleModal";
import EditNotesModal from "./EditNotesModal";

dayjs.extend(utc);
dayjs.extend(tz);

const STATUS_STYLES = {
  CONFIRMED: "bg-green-100 text-green-800",
  PENDING: "bg-yellow-100 text-yellow-800",
  CANCELLED: "bg-gray-200 text-gray-600",
};

const fmtDate = (iso, zone) =>
  iso ? dayjs(iso).tz(zone || "America/Chicago").format("ddd, MMM D, YYYY") : "—";
const fmtTime = (iso, zone) =>
  iso ? dayjs(iso).tz(zone || "America/Chicago").format("h:mm A") : "—";

const MyBookings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = searchParams.get("status");
  const [banner, setBanner] = useState(null);

  const { data: bookingData, loading: bookingsLoading, refetch } = useQuery(
    LIST_MY_BOOKINGS,
    { fetchPolicy: "network-only" }
  );
  const { data: eventsData, loading: eventsLoading } = useQuery(LIST_EVENTS, {
    fetchPolicy: "cache-first",
  });
  const [cancelBooking, { loading: cancelling }] = useMutation(CANCEL_BOOKING);
  const { data: policyData } = useQuery(GET_BOOKING_POLICY, {
    fetchPolicy: "cache-first",
  });
  const cancelWindowHours = policyData?.getBookingPolicy?.cancelWindowHours ?? 24;

  const [rescheduleBooking, setRescheduleBooking] = useState(null);
  const [editBooking, setEditBooking] = useState(null);

  const eventMap = useMemo(() => {
    const map = {};
    (eventsData?.listEvents?.items || []).forEach((e) => (map[e.id] = e));
    return map;
  }, [eventsData]);

  const bookings = useMemo(() => {
    const items = bookingData?.listMyBookings?.items || [];
    return [...items].sort(
      (a, b) => new Date(b.startTime) - new Date(a.startTime)
    );
  }, [bookingData]);

  useEffect(() => {
    if (statusParam === "success") {
      setBanner({
        kind: "success",
        text: "Deposit received — you're booked in. A confirmation is on its way to your inbox.",
      });
      // Strip the query param so refresh doesn't re-show.
      const next = new URLSearchParams(searchParams);
      next.delete("status");
      next.delete("bookingId");
      setSearchParams(next, { replace: true });
    } else if (statusParam === "cancel") {
      setBanner({
        kind: "warn",
        text: "Payment was cancelled — your slot is no longer reserved.",
      });
      const next = new URLSearchParams(searchParams);
      next.delete("status");
      next.delete("bookingId");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusParam]);

  const handleCancel = async (id) => {
    if (!window.confirm("Cancel this booking? Your slot will be released.")) return;
    try {
      await cancelBooking({ variables: { id } });
      await refetch();
    } catch (err) {
      alert(err?.message || "Could not cancel — try again.");
    }
  };

  const loading = bookingsLoading || eventsLoading;

  return (
    <div className="w-[100%] mx-auto mb-[63px] min-h-screen">
      <div className="w-[90%] xl:max-w-[1300px] flex flex-col mx-auto pt-[121px]">
        <div className="mb-10">
          <h1 className="buy_and_sell_title">My Bookings</h1>
          <p className="buy_and_sell_subtitle max-w-[600px]">
            Your fitting clinic appointments. Reschedule or cancel up to{" "}
            {cancelWindowHours} hours before your appointment — after that, contact
            SaddleFit. Missed appointments require full payment to rebook.
          </p>
        </div>

        {banner && (
          <div
            className={`mb-6 px-4 py-3 rounded-lg text-[14px] font-[Montserrat] ${
              banner.kind === "success"
                ? "bg-green-50 text-green-800 border border-green-200"
                : "bg-yellow-50 text-yellow-900 border border-yellow-200"
            }`}
          >
            {banner.text}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center h-[400px]">
            <Loader filledColor="#2b364b" className="!h-[60px]" />
          </div>
        ) : bookings.length === 0 ? (
          <div className="text-center py-20 font-[Montserrat] text-[#6b7280]">
            <p className="text-[16px]">You haven&apos;t booked any fittings yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-[#EAEBED] bg-white">
            <table className="w-full font-[Montserrat] text-[14px]">
              <thead className="bg-[#F1F1F0] text-[#2b364b] text-left">
                <tr>
                  <th className="px-4 py-3">Event</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Time</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Payment</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => {
                  const event = eventMap[b.eventId];
                  const zone = event?.timezone || "America/Chicago";
                  const isCancelled = b.status === "CANCELLED";
                  const isPast = new Date(b.startTime) < new Date();
                  const canEdit = !isCancelled && !isPast && b.status === "CONFIRMED";
                  // Inside the lockout window, self-service reschedule/cancel are
                  // disabled (customer must contact SaddleFit). Editing notes stays open.
                  const hoursUntil =
                    (new Date(b.startTime).getTime() - Date.now()) / 3.6e6;
                  const withinWindow = hoursUntil < cancelWindowHours;
                  return (
                    <tr key={b.id} className="border-t border-[#EAEBED]">
                      <td className="px-4 py-3 align-top">
                        <div className="font-semibold text-[#2b364b]">
                          {event?.eventName || "Fitting"}
                        </div>
                        {event?.eventLocation && (
                          <div className="text-[12px] text-[#6b7280]">
                            {event.eventLocation}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top whitespace-nowrap">
                        {fmtDate(b.startTime, zone)}
                      </td>
                      <td className="px-4 py-3 align-top whitespace-nowrap">
                        {fmtTime(b.startTime, zone)}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <span
                          className={`px-2 py-1 rounded-full text-[11px] font-semibold ${
                            STATUS_STYLES[b.status] || ""
                          }`}
                        >
                          {b.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-top whitespace-nowrap">
                        {(() => {
                          const fullUpfront =
                            b.scanAmount != null &&
                            b.balanceDue === 0 &&
                            b.depositAmount === b.scanAmount &&
                            b.scanAmount > 0;
                          if (b.depositPaidAt) {
                            const amt = ((b.depositAmount || 0) / 100).toFixed(2);
                            return fullUpfront
                              ? `Full $${amt} paid`
                              : `$${amt} paid`;
                          }
                          if (b.depositAmount) {
                            return fullUpfront
                              ? `Full $${(b.depositAmount / 100).toFixed(2)} due`
                              : `$${(b.depositAmount / 100).toFixed(2)} due`;
                          }
                          return "—";
                        })()}
                      </td>
                      <td className="px-4 py-3 align-top text-right whitespace-nowrap">
                        {canEdit ? (
                          <div className="flex flex-col items-end gap-1.5">
                            <div className="flex gap-2 justify-end">
                              {!withinWindow && (
                                <button
                                  onClick={() => setRescheduleBooking({ booking: b, event })}
                                  className="px-3 py-1 rounded-full border border-[#2b364b] text-[#2b364b] text-[12px] hover:bg-[#f1f3f5]"
                                >
                                  Reschedule
                                </button>
                              )}
                              <button
                                onClick={() => setEditBooking(b)}
                                className="px-3 py-1 rounded-full border border-[#5c80b6] text-[#5c80b6] text-[12px] hover:bg-[#f1f3f5]"
                              >
                                Edit
                              </button>
                              {!withinWindow && (
                                <button
                                  disabled={cancelling}
                                  onClick={() => handleCancel(b.id)}
                                  className="px-3 py-1 rounded-full border border-red-300 text-red-600 text-[12px] hover:bg-red-50 disabled:opacity-50"
                                >
                                  Cancel
                                </button>
                              )}
                            </div>
                            {withinWindow && (
                              <span className="text-[11px] text-[#6b7280] max-w-[200px] text-right">
                                Within {cancelWindowHours}h — contact SaddleFit to change or cancel.
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-[12px] text-[#6b7280]">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {rescheduleBooking && (
        <RescheduleModal
          isOpen={!!rescheduleBooking}
          booking={rescheduleBooking.booking}
          event={rescheduleBooking.event}
          onClose={() => setRescheduleBooking(null)}
          onDone={() => {
            setRescheduleBooking(null);
            refetch();
          }}
        />
      )}

      {editBooking && (
        <EditNotesModal
          isOpen={!!editBooking}
          booking={editBooking}
          onClose={() => setEditBooking(null)}
          onDone={() => {
            setEditBooking(null);
            refetch();
          }}
        />
      )}
    </div>
  );
};

export default MyBookings;
